/**
 * AppShell — 1등급 MVP 앱 셸
 * 헤더 + 풋바 + Outlet. 모드(개인/학원)에 따라 풋바가 전환된다.
 */
import React, { useState } from 'react';
import { Outlet, Navigate, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
  IoHomeOutline, IoSchoolOutline, IoMapOutline,
  IoChatbubbleOutline, IoPersonOutline,
  IoCalendarOutline, IoPeopleOutline, IoChevronDown,
  IoCardOutline,
} from 'react-icons/io5';
import { useAcademy } from '../../context/AcademyContext';
import { useAuth } from '../../context/AuthContext';
import { resolveAccountType } from '../../services/userProfileService';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase/config';
import LocationPicker, { loadLocation, labelOf } from '../common/LocationPicker';

const PERSONAL_TABS = [
  { key: 'home', label: '홈', path: '/app/home', Icon: IoHomeOutline },
  { key: 'my', label: '내 학원', path: '/app/my', Icon: IoSchoolOutline },
  { key: 'map', label: '지도', path: '/app/map', Icon: IoMapOutline },
  { key: 'profile', label: '마이', path: '/app/profile', Icon: IoPersonOutline },
];

const ACADEMY_TABS = [
  { key: 'biz', label: '운영홈', path: '/app/biz', Icon: IoHomeOutline },
  { key: 'classes', label: '수업', path: '/app/biz/classes', Icon: IoSchoolOutline },
  { key: 'schedule', label: '일정', path: '/app/biz/schedule', Icon: IoCalendarOutline },
  { key: 'students', label: '학생', path: '/app/biz/students', Icon: IoPeopleOutline },
  { key: 'pay', label: '결제', path: '/app/biz/pay', Icon: IoCardOutline },
  { key: 'bizmy', label: '마이', path: '/app/profile', Icon: IoPersonOutline },
];

const AppShell = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { mode, setMode } = useAcademy();
  const { currentUser, userData } = useAuth();
  const academyMode = mode === 'academy';
  // 학원모드 첫 화면 = 운영홈(BizHomePage)
  const academyHomePath = '/app/biz';
  const tabs = academyMode ? ACADEMY_TABS : PERSONAL_TABS;
  const isDemo = userData?.provider === 'demo';

  // 지역 선택
  const [location, setLocation] = useState(() => loadLocation());
  const [pickerOpen, setPickerOpen] = useState(false);

  // 학원 상세 = 풀스크린 모드 (헤더/풋바 숨김, 사진이 화면 끝까지)
  const fullscreen = /^\/app\/academy\/[^/]+/.test(pathname);

  const exitDemo = async () => {
    try { await signOut(auth); } catch {}
    try { localStorage.removeItem('topgrade.demoSession'); } catch {}
    setMode('personal');
    navigate('/app/home');
  };

  // 로그인했는데 가입유형(학부모/학원) 미선택 → 선택 화면으로
  const needsAccountType =
    currentUser && userData && userData.provider !== 'demo' &&
    !resolveAccountType(userData);
  if (needsAccountType) {
    return <Navigate to="/app/account-type" replace />;
  }

  return (
    <Wrap>
      {!fullscreen && (
        <Header>
          <BrandRow>
            <Brand onClick={() => navigate(academyMode ? academyHomePath : '/app/home')}>
              <BrandLogo src="/logo.png" alt="1등급" />
              <BrandName>{academyMode ? '1등급 학원' : '1등급'}</BrandName>
            </Brand>
            {!academyMode && (
              <LocBtn type="button" onClick={() => setPickerOpen(true)}>
                {labelOf(location)} <IoChevronDown size={14} />
              </LocBtn>
            )}
          </BrandRow>
          <Actions>
            <IconBtn onClick={() => navigate('/app/chat')} aria-label="채팅">
              <IoChatbubbleOutline size={22} />
            </IconBtn>
          </Actions>
        </Header>
      )}

      {!fullscreen && isDemo && (
        <DemoBar>
          <DemoText>가상 체험 공간입니다. 자유롭게 이용해 보세요</DemoText>
          <DemoExitBtn type="button" onClick={exitDemo}>체험 종료</DemoExitBtn>
        </DemoBar>
      )}

      <Body $fullscreen={fullscreen}>
        <Outlet />
      </Body>

      {!fullscreen && (
        <Nav>
          {tabs.map(({ key, label, path, Icon }) => {
            const active = pathname === path;
            return (
              <Tab key={key} $active={active} onClick={() => navigate(path)}>
                <Icon size={23} />
                <TabLabel>{label}</TabLabel>
              </Tab>
            );
          })}
        </Nav>
      )}

      {pickerOpen && (
        <LocationPicker
          initial={location}
          onClose={() => setPickerOpen(false)}
          onApply={setLocation}
        />
      )}
    </Wrap>
  );
};

export default AppShell;

const Wrap = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.background.default};
`;

const Header = styled.header`
  height: 56px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 18px;
  background: #fff;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
`;

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const Brand = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
`;

const LocBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 6px 12px;
  border-radius: 999px;
  background: #F2F3F7;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
  font-weight: 700;
  max-width: 140px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const BrandLogo = styled.img`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  object-fit: contain;
`;

const BrandName = styled.span`
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.5px;
  color: ${({ theme }) => theme.colors.primary};
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const IconBtn = styled.button`
  background: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  padding: 4px;
`;

const DemoBar = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 9px 16px;
  background: #FEF3C7;
  border-bottom: 1px solid #FDE68A;
`;

const DemoText = styled.span`
  font-size: 12.5px;
  font-weight: 700;
  color: #92400E;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DemoExitBtn = styled.button`
  flex-shrink: 0;
  padding: 5px 12px;
  border-radius: 13px;
  background: #fff;
  border: 1px solid #FDE68A;
  font-size: 12px;
  font-weight: 700;
  color: #92400E;
`;

const Body = styled.main`
  flex: 1;
  overflow-y: auto;
  padding-bottom: ${({ $fullscreen }) => ($fullscreen ? '0' : '72px')};
`;

const Nav = styled.nav`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 64px;
  display: flex;
  background: #fff;
  border-top: 1px solid ${({ theme }) => theme.colors.divider};
  max-width: 480px;
  margin: 0 auto;
`;

const Tab = styled.button`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  background: none;
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.text.hint)};
`;

const TabLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
`;

